import { RESAMPLE_HZ } from './constants';

const cubicInterpolate = (
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  t: number
) => {
  const a = -0.5 * p0 + 1.5 * p1 - 1.5 * p2 + 0.5 * p3;
  const b = p0 - 2.5 * p1 + 2 * p2 - 0.5 * p3;
  const c = -0.5 * p0 + 0.5 * p2;
  const d = p1;
  return a * t * t * t + b * t * t + c * t + d;
};

/**
 * Resample irregular (timestamp, value) pairs to uniform rate with cubic spline.
 */
export const resampleCubicUniform = (
  timestamps: number[],
  values: number[],
  targetHz: number = RESAMPLE_HZ
): number[] => {
  if (timestamps.length === 0 || values.length === 0) return [];
  if (timestamps.length === 1) {
    const durationSec =
      Math.max(1, Math.round((timestamps[0] % 1000) / 1000)) || 300;
    return Array.from({ length: durationSec * targetHz }, () => values[0]);
  }

  const start = timestamps[0];
  const end = timestamps[timestamps.length - 1];
  const durationMs = Math.max(1000, end - start);
  const sampleCount = Math.max(2, Math.floor((durationMs / 1000) * targetHz));
  const output: number[] = [];

  for (let index = 0; index < sampleCount; index += 1) {
    const targetTime = start + (index / targetHz) * 1000;

    let segmentIndex = 0;
    while (
      segmentIndex < timestamps.length - 1 &&
      timestamps[segmentIndex + 1] < targetTime
    ) {
      segmentIndex += 1;
    }

    const index1 = Math.max(0, segmentIndex);
    const index2 = Math.min(timestamps.length - 1, index1 + 1);
    const index0 = Math.max(0, index1 - 1);
    const index3 = Math.min(timestamps.length - 1, index2 + 1);

    const t1 = timestamps[index1];
    const t2 = timestamps[index2];

    const span = t2 - t1 || 1;
    const localT = (targetTime - t1) / span;

    output.push(
      cubicInterpolate(
        values[index0],
        values[index1],
        values[index2],
        values[index3],
        Math.min(1, Math.max(0, localT))
      )
    );
  }

  return output;
};
